import React, { useEffect, useRef, useState } from 'react';

const TRIGGER_STYLES = {
  ghost: {
    base: 'p-1.5 rounded-md',
    idle: 'text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-100',
    open: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-100',
  },
  outline: {
    base: 'p-2.5 rounded-lg border border-slate-300 dark:border-slate-600',
    idle: 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100',
    open: 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-slate-100',
  },
};

/**
 * The "…" menu that keeps rarely used actions out of a cramped row.
 *
 * items: [{ label, icon?, onClick, danger?, disabled?, separatorBefore? }]
 * variant: 'ghost' for dense rows, 'outline' when it sits next to a real button
 * and has to read as one.
 */
export function OverflowMenu({ items, label = 'Weitere Aktionen', align = 'right', variant = 'ghost', className = '' }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const trigger = TRIGGER_STYLES[variant] || TRIGGER_STYLES.ghost;

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const usable = items.filter(Boolean);
  if (usable.length === 0) return null;

  return (
    <div ref={wrapperRef} className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${trigger.base} transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
          open ? trigger.open : trigger.idle
        }`}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-30 mt-1 min-w-48 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {usable.map((item, index) => (
            <React.Fragment key={item.label}>
              {item.separatorBefore && index > 0 && (
                <div className="my-1 h-px bg-slate-100 dark:bg-slate-700" />
              )}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40 ${
                  item.danger
                    ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30'
                    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
